import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import Icon from 'qwc2/components/Icon';
import SideBar from 'qwc2/components/SideBar';
import LocaleUtils from 'qwc2/utils/LocaleUtils';
import {setEditContext, clearEditContext} from 'qwc2/actions/editing';
import {setCurrentTask} from 'qwc2/actions/task';
import {addDetailWindow} from '../actions/details';
import RoleUtils from '../utils/RoleUtils';
import '../style/AddFeature.css';

const EDIT_CONTEXT_ID = 'AddFeature';

class AddFeature extends React.Component {
    static propTypes = {
        addDetailWindow: PropTypes.func,
        clearEditContext: PropTypes.func,
        editContext: PropTypes.object,
        mapProjection: PropTypes.string,
        objectTypes: PropTypes.array,
        role: PropTypes.string,
        setCurrentTask: PropTypes.func,
        setEditContext: PropTypes.func
    }

    static defaultProps = {
        objectTypes: [],
        role: 'editor'
    }

    state = {
        selectedType: null
    }

    componentDidMount() {
        this.updateButtonVisibility();
    }

    componentDidUpdate(prevProps) {
        this.updateButtonVisibility();

        const editContext = this.props.editContext;
        const prevEditContext = prevProps.editContext;
        const hasNewGeometry = editContext && editContext.changed && editContext.feature && editContext.feature.geometry;
        const isNewChange = !prevEditContext || !prevEditContext.changed;
        if (this.state.selectedType && hasNewGeometry && isNewChange) {
            this.createFeature(editContext.feature.geometry);
        }
    }

    // qwc2's TaskButton/AppMenu have no per-plugin visibility hook, so this plugin
    // marks <body> itself, and AddFeature.css hides its own toolbar/menu entry accordingly
    updateButtonVisibility = () => {
        document.body.classList.toggle('addfeature-allowed', RoleUtils.hasAnyRole([this.props.role]));
    }

    startDrawing = (type) => {
        this.setState({selectedType: type});
        // qwc2's editing reducer derives geomType and permissions from the editConfig
        this.props.setEditContext(EDIT_CONTEXT_ID, {
            action: 'Draw',
            editConfig: {geomType: type.geometryType || 'Point', permissions: {creatable: true, updatable: true}},
            feature: null,
            changed: false
        });
    }

    createFeature = (geometry) => {
        const type = this.state.selectedType;
        this.props.addDetailWindow({
            detailWindowType: 'newFeature',
            newFeatureLabel: type.label,
            newFeatureRoles: [this.props.role],
            properties: {
                objectType: type.type,
                id: ''
            },
            geometry: geometry,
            geometryCrs: this.props.mapProjection
        });
        this.reset();
    }

    reset = () => {
        this.props.clearEditContext(EDIT_CONTEXT_ID);
        this.setState({selectedType: null});
        this.props.setCurrentTask(null);
    }

    geometryIcon = (type) => {
        const geomType = String(type.geometryType || 'Point');
        if (geomType.includes('Polygon')) {
            return 'polygon';
        } else if (geomType.includes('Line')) {
            return 'line';
        }
        return 'point';
    }

    renderBody = () => {
        if (this.state.selectedType) {
            const type = this.state.selectedType;
            return (
                <div className="addfeature-body">
                    <div className="addfeature-selected">
                        <Icon icon={this.geometryIcon(type)} />
                        <span className="addfeature-typelist-label">{type.label || type.type}</span>
                    </div>
                    <div className="addfeature-hint addfeature-drawhint">{LocaleUtils.tr("addfeature.drawhint")}</div>
                    <button className="button addfeature-cancel" onClick={this.reset} type="button">{LocaleUtils.tr("addfeature.cancel")}</button>
                </div>
            );
        }
        return (
            <div className="addfeature-body">
                <div className="addfeature-hint">{LocaleUtils.tr("addfeature.selecttype")}</div>
                <ul className="addfeature-typelist">
                    {this.props.objectTypes.map(type => (
                        <li key={type.type} onClick={() => this.startDrawing(type)}>
                            <Icon icon={this.geometryIcon(type)} />
                            <span className="addfeature-typelist-label">{type.label || type.type}</span>
                            <Icon className="addfeature-typelist-arrow" icon="chevron-right" />
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    render() {
        if (!RoleUtils.hasAnyRole([this.props.role])) {
            return null;
        }
        return (
            <SideBar icon="addfeature" id="AddFeature" onHide={this.reset} title={LocaleUtils.tr("addfeature.title")} width="18em">
                {() => ({body: this.renderBody()})}
            </SideBar>
        );
    }
}

const selector = (state) => ({
    editContext: state.editing.contexts[EDIT_CONTEXT_ID],
    mapProjection: state.map.projection
});

export default connect(selector, {
    addDetailWindow: addDetailWindow,
    clearEditContext: clearEditContext,
    setCurrentTask: setCurrentTask,
    setEditContext: setEditContext
})(AddFeature);
